// Schreiben-Assistent Etappe 5 (#586): bedingte Abschnitte {{#wenn FELD [=|!= Wert]}} … {{#sonst}} … {{/wenn}} und
// Berechnungen {{= AUSDRUCK}}. Reine Funktionen; Werte kommen über wert(schluessel) („eingabe.x“ oder „gruppe.feld“).
// Spec: docs/superpowers/specs/2026-10-31-schreiben-assistent-design.md (Etappe 5)

export type WertQuelle = (schluessel: string) => string | undefined;

/** Eingabe mit optionaler Deklaration „eingabe.k|Beschriftung|typ“ oder Empfängerwert „gruppe.feld“. */
// Schlüssel nur \w (ein „-“ danach ist Rechnen: eingabe.frist-7); Auswahl-Optionen dürfen Leerzeichen enthalten
export const FELD_REF = /eingabe\.\w+(?:\|[^|}]*\|(?:text|datum|betrag|janein|ankreuzen|auswahl:[^|}=!]*[^|}=!\s]))?|[a-z_]+(?:\.[a-z_]+)+/i;
/** „eingabe.k|L|typ“ → „eingabe.k“ */
export const feldSchluessel = (ref: string) => { const k = ref.split('|')[0].trim(); return k.startsWith('eingabe.') ? k : k.toLowerCase(); };

const BEDINGUNG = new RegExp(`^\\s*(${FELD_REF.source})\\s*(?:(!=|==?)\\s*(.*?))?\\s*$`, 'i');
const MARKE = /\{\{\s*#wenn\s+([^}]*)\}\}|\{\{\s*#sonst\s*\}\}|\{\{\s*\/wenn\s*\}\}/g;

type Knoten = string | { bedingung: string; ja: Knoten[]; nein: Knoten[]; roh: string };

const norm = (s: string | undefined) => (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const gesetzt = (s: string | undefined) => !!s?.trim() && norm(s) !== 'nein';

type Melder = { beiFeld?: (k: string, v: string | undefined) => void; beiUnklar?: (bedingung: string) => void };

/** Bedingung auswerten; beiFeld meldet jedes gelesene Feld (für „fehlt“), beiUnklar eine unlesbare Bedingung (gilt als falsch). */
function pruefe(bedingung: string, wert: WertQuelle, melder: Melder): boolean {
  const m = BEDINGUNG.exec(bedingung);
  if (!m) { melder.beiUnklar?.(bedingung); return false; }
  const k = feldSchluessel(m[1]);
  const v = wert(k);
  melder.beiFeld?.(k, v);
  if (!m[2]) return gesetzt(v);
  const gleich = norm(v) === norm(m[3]);
  return m[2] === '!=' ? !gleich : gleich;
}

/** Text in Knoten zerlegen. Steht eine Marke allein auf ihrer Zeile, gehört der Zeilenumbruch danach dazu. */
function zerlege(text: string): { knoten: Knoten[]; ok: boolean } {
  const wurzel: Knoten[] = [];
  const stapel: { k: Extract<Knoten, object>; imSonst: boolean; start: number }[] = [];
  const ziel = () => (stapel.length ? (stapel[stapel.length - 1].imSonst ? stapel[stapel.length - 1].k.nein : stapel[stapel.length - 1].k.ja) : wurzel);
  let pos = 0, ok = true;
  for (const m of text.matchAll(MARKE)) {
    const anfang = m.index ?? 0;
    let ende = anfang + m[0].length;
    const zeilenAnfang = anfang === 0 || text[anfang - 1] === '\n';
    if (zeilenAnfang && text[ende] === '\n') ende++;
    if (anfang > pos) ziel().push(text.slice(pos, anfang));
    pos = ende;
    if (m[1] !== undefined) {
      const k = { bedingung: m[1], ja: [] as Knoten[], nein: [] as Knoten[], roh: '' };
      ziel().push(k);
      stapel.push({ k, imSonst: false, start: anfang });
    } else if (/#sonst/.test(m[0])) {
      if (stapel.length) stapel[stapel.length - 1].imSonst = true; else { ok = false; ziel().push(m[0]); }
    } else if (stapel.length) stapel.pop();
    else { ok = false; ziel().push(text.slice(anfang, ende)); }
  }
  if (pos < text.length) ziel().push(text.slice(pos));
  if (stapel.length) return { knoten: [text], ok: false }; // offener Abschnitt: Text unverändert lassen
  return { knoten: wurzel, ok };
}

function baue(knoten: Knoten[], wert: WertQuelle, melder: Melder): string {
  return knoten.map((k) => (typeof k === 'string' ? k : baue(pruefe(k.bedingung, wert, melder) ? k.ja : k.nein, wert, melder))).join('');
}

/** Abschnitte auflösen (von außen nach innen – Bedingungen in weggelassenen Abschnitten werden nicht gelesen). */
export function loeseAbschnitte(text: string, wert: WertQuelle, beiFeld?: (k: string, v: string | undefined) => void, beiUnklar?: (bedingung: string) => void): string {
  if (!/\{\{\s*(#wenn|#sonst|\/wenn)/.test(text)) return text;
  text = text.replace(/\r\n/g, '\n');
  const { knoten } = zerlege(text);
  const aus = baue(knoten, wert, { beiFeld, beiUnklar }).replace(/\n{3,}/g, '\n\n');
  // fiel ein Abschnitt am Ende weg, keine Leerzeilen zurücklassen
  return /\n$/.test(text) ? aus : aus.replace(/\n+$/, '');
}

/** Übrig gebliebene Marken (fehlendes {{/wenn}} o. Ä.) – für die Fehlerliste. */
export function abschnittFehler(text: string): string | null {
  if (/\{\{\s*#wenn/.test(text)) return 'Abschnitt ohne {{/wenn}}';
  if (/\{\{\s*(#sonst|\/wenn)\s*\}\}/.test(text)) return '{{/wenn}} ohne passendes {{#wenn …}}';
  return null;
}

// ---------- Berechnungen ----------

type Wert = { art: 'zahl' | 'euro' | 'datum'; n: number };
const TAG = 86_400_000;

/** Deutscher Betrag („1.234,56 €“), Zahl („12,5“) oder Datum („TT.MM.JJJJ“, n = Tage seit 1970). */
export function zahlAus(s: string): Wert | null {
  const t = s.trim();
  const d = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(t);
  if (d) {
    const iso = `${d[3]}-${d[2]}-${d[1]}`, ms = Date.parse(`${iso}T00:00:00Z`);
    return Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== iso ? null : { art: 'datum', n: Math.round(ms / TAG) };
  }
  const euro = /€|\bEUR\b/i.test(t);
  const roh = t.replace(/€|\bEUR\b/gi, '').replace(/\s/g, '');
  if (!/^-?\d{1,3}(?:\.\d{3})*(?:,\d+)?$|^-?\d+(?:,\d+)?$/.test(roh)) return null;
  const n = Number(roh.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? { art: euro ? 'euro' : 'zahl', n } : null;
}

const formatiere = (w: Wert): string => {
  if (w.art === 'datum') { const d = new Date(w.n * TAG); return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`; }
  if (w.art === 'euro') return `${w.n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  return w.n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
};

class RechenFehler extends Error {}

function verknuepfe(a: Wert, op: string, b: Wert): Wert {
  if (a.art === 'datum' || b.art === 'datum') {
    if (op === '+' && a.art === 'datum' && b.art !== 'datum') return { art: 'datum', n: a.n + Math.round(b.n) };
    if (op === '+' && b.art === 'datum' && a.art !== 'datum') return { art: 'datum', n: b.n + Math.round(a.n) };
    if (op === '-' && a.art === 'datum' && b.art !== 'datum') return { art: 'datum', n: a.n - Math.round(b.n) };
    if (op === '-' && a.art === 'datum' && b.art === 'datum') return { art: 'zahl', n: a.n - b.n };
    throw new RechenFehler('Mit einem Datum geht nur + und − Tage oder Datum − Datum');
  }
  if (op === '/' && b.n === 0) throw new RechenFehler('Division durch 0');
  const n = op === '+' ? a.n + b.n : op === '-' ? a.n - b.n : op === '*' ? a.n * b.n : a.n / b.n;
  const art = op === '/' && a.art === 'euro' && b.art === 'euro' ? 'zahl' : a.art === 'euro' || b.art === 'euro' ? 'euro' : 'zahl';
  return { art, n: art === 'euro' ? Math.round(n * 100) / 100 : n };
}

const TOKEN = new RegExp(`\\s*(?:(${FELD_REF.source})|(\\d[\\d.]*(?:,\\d+)?)|([-+*/()]))`, 'iy');

/** Ausdruck ausrechnen: Ergebnis als Text, oder fehlende Felder, oder Fehler. */
export function rechne(ausdruck: string, wert: WertQuelle): { wert: string } | { fehlt: string[] } | { fehler: string } {
  const tokens: { feld?: string; zahl?: string; op?: string }[] = [];
  TOKEN.lastIndex = 0;
  const rest = ausdruck.trim();
  while (TOKEN.lastIndex < rest.length) {
    const vorher = TOKEN.lastIndex;
    const m = TOKEN.exec(rest);
    if (!m || TOKEN.lastIndex === vorher) {
      if (!rest.slice(vorher).trim()) break;
      return { fehler: `Unverständlich: „${rest.slice(vorher).trim()}“` };
    }
    tokens.push(m[1] ? { feld: feldSchluessel(m[1]) } : m[2] ? { zahl: m[2] } : { op: m[3] });
  }
  const fehlt: string[] = [];
  let i = 0;
  const faktor = (): Wert => {
    const t = tokens[i++];
    if (!t) throw new RechenFehler('Ausdruck endet zu früh');
    if (t.op === '-') { const w = faktor(); if (w.art === 'datum') throw new RechenFehler('Minus vor einem Datum geht nicht'); return { ...w, n: -w.n }; }
    if (t.op === '(') { const w = summe(); if (tokens[i++]?.op !== ')') throw new RechenFehler('Klammer nicht geschlossen'); return w; }
    if (t.zahl) { const w = zahlAus(t.zahl); if (!w) throw new RechenFehler(`Keine Zahl: ${t.zahl}`); return w; }
    if (t.feld) {
      const roh = wert(t.feld);
      if (!roh?.trim()) { if (!fehlt.includes(t.feld)) fehlt.push(t.feld); return { art: 'zahl', n: 1 }; }
      const w = zahlAus(roh);
      if (!w) throw new RechenFehler(`${t.feld} ist keine Zahl und kein Datum („${roh}“)`);
      return w;
    }
    throw new RechenFehler(`Unerwartet: ${t.op}`);
  };
  const produkt = (): Wert => { let w = faktor(); while (tokens[i]?.op === '*' || tokens[i]?.op === '/') { const op = tokens[i++].op!; w = verknuepfe(w, op, faktor()); } return w; };
  const summe = (): Wert => { let w = produkt(); while (tokens[i]?.op === '+' || tokens[i]?.op === '-') { const op = tokens[i++].op!; w = verknuepfe(w, op, produkt()); } return w; };
  try {
    const w = summe();
    if (i < tokens.length) throw new RechenFehler('Zu viel am Ende des Ausdrucks');
    return fehlt.length ? { fehlt } : { wert: formatiere(w) };
  } catch (e) {
    if (fehlt.length) return { fehlt };
    if (e instanceof RechenFehler) return { fehler: e.message };
    throw e;
  }
}
